import axios from "axios";
import { get } from "lodash";
import { PolicyType, SERVICE_CENTER_BASE_URL } from "./constants";
import { COOKIE_NAMES, deleteCookie, setCookie } from "./cookies";

export type AxleServiceError = {
  code: number | null;
  message: string | null;
  errorCode?: string;
  reportContext?: { documentClassification?: string };
};

const getString = (body: unknown, path: string) => {
  const value = get(body, path);
  return typeof value === "string" ? value : undefined;
};

// Copies the error code and reportContext from a failed response for the report; non-string values are dropped.
const getErrorReportFields = (body: unknown) => {
  const documentClassification = getString(
    body,
    "reportContext.documentClassification"
  );
  return {
    errorCode: getString(body, "errorCode"),
    ...(documentClassification && {
      reportContext: { documentClassification },
    }),
  };
};

const ibBaseUrl = `${SERVICE_CENTER_BASE_URL}/ignition-backend`;

export enum Operation {
  SETUP = "setup",
  LOGIN = "login",
  GET_MFA_OPTIONS = "getMfaOptions",
  ENTER_MFA = "enterMfa",
  SEND_MFA = "sendMfa",
  GET_ACCOUNT = "getAccount",
  GET_POLICY = "getPolicy",
  // HITL re-entry/resend ops — recover a suspended login-/MFA-wait on the SAME
  // run (no new browser-agent run). They map back to login / enterMfa server-side.
  SUBMIT_CREDENTIALS = "submitCredentials",
  RESEND_MFA = "resendMfa"
}

type LoginOperation = {
  ignitionToken: string,
  loginUrl: string,
  username?: string,
  password?: string,
  zipcode?: string,
  account?: string,
  token?: string,
}

type GetMfaOptionsOperation = {

}

type SendMfaOperation = {
  ignitionToken: string,
  integration: string,
  selectedMfaOption: string
}

type EnterMfaOperation = {
  code: string
}

// Re-enter credentials (or switch carrier) into a login-wait. Exactly one of:
// a pre-vaulted credentialId, or raw username+password.
//
// `submissionId` is this submit's idempotency key and MUST be freshly generated
// for every submit the human makes. The old key was the wait round (`attempt`),
// which cannot advance while login is still running: the re-entry poll returns
// the same `awaiting` row, the form re-prompts on that stale round, and the next
// submit reused a consumed key — so it came back `deduplicated` and was dropped
// while the run sat on its callback. One key per submit action, stable only
// across the backend's own retries of that one dispatch.
type SubmitCredentialsOperation = {
  credentialId?: string,
  username?: string,
  password?: string,
  zipcode?: string,
  loginUrl?: string,
  integration?: string,
  submissionId: string,
}

type GetPolicyOperation = {
  // Nullable: label-only policy selections (no policy number on the carrier
  // account) send this as null — see hitlAiInputSchema.ts's getPolicyData.
  policyNumber?: string | null,
  policyType?: string,
  policyLabel?: string,
  policyHolder?: string,
}

// Resend a fresh MFA code into a code-wait. `attempt` keeps repeated resends
// from deduping against each other.
type ResendMfaOperation = {
  attempt: number,
}

// A non-terminal awaiting state surfaced by hitlAi (the data element of the
// processDirectLink tuple). The UI branches on it to re-prompt for credentials
// (operation:"login"), a corrected/resent MFA code (operation:"enterMfa"), a
// still-finalizing policy selection (operation:"getPolicy", handled in Confirm.js),
// or — with no re-entry prompt of its own — a still-running getMfaOptions poll
// (SendMfa.js just retries).
export type AwaitingResponse = {
  status: "awaiting",
  operation: "login" | "enterMfa" | "getPolicy" | "getMfaOptions",
  awaitingReason?: string,
  attempt?: number,
  mfaRejected?: boolean,
}

/** Narrow a processDirectLink data payload to an awaiting state. */
export const isAwaiting = (data: unknown): data is AwaitingResponse =>
  !!data &&
  typeof data === "object" &&
  (data as { status?: unknown }).status === "awaiting";

/**
 * The phase is still running: hitlAi's poll budget elapsed before it reached a
 * terminal state. Callers re-issue the same operation to keep polling.
 */
export const isStarted = (data: unknown): boolean =>
  !!data &&
  typeof data === "object" &&
  (data as { status?: unknown }).status === "started";

// Only updateIgnition opts into this — it's a fast DB write with no reason to
// hang (see ignition-backend/serverless.yml provider.timeout: 30). Most
// fetchClient callers go through hitlAi's /direct-link, which is provisioned
// for up to 900s because it blocks on a live browser-agent workflow — do NOT
// make this the default or it will kill those legitimately long-running polls.
const UPDATE_IGNITION_TIMEOUT_MS = 30_000;

const fetchClient = async (
  url: string,
  options: Record<string, any> = {},
  timeoutMs?: number
): Promise<[any, AxleServiceError | null]> => {
  const controller = timeoutMs ? new AbortController() : null;
  const timer = controller
    ? setTimeout(() => controller.abort(), timeoutMs)
    : null;

  try {
    const response = await fetch(
      url,
      controller ? { ...options, signal: controller.signal } : options
    );

    const body = await response.json();

    if (!response.ok) {
      return [
        null,
        {
          code: get(response, "status", 500),
          message: get(body, "message", null),
          ...getErrorReportFields(body),
        },
      ];
    }

    const data = body.data;
    return [data, null]; // Success response with no error
  } catch (error: any) {
    if (error?.name === "AbortError") {
      // Do not log `url` here — several callers (e.g. updateIgnition) embed
      // the ignitionToken directly in the path.
      console.error("[fetchClient] Request timed out");
      return [null, { code: 504, message: "Request timed out" }];
    }

    if (!error.response) {
      // Network error or connection was interrupted
      console.error("Connection interrupted");
      return [null, { code: null, message: "Connection interrupted" }];
    }

    return [
      null,
      { code: get(error, "status", 500), message: get(error, "message", null) },
    ]; // Return null data with error object
  } finally {
    if (timer) clearTimeout(timer);
  }
};

const axiosClient = async (
  url: string,
  options = {}
): Promise<[any, AxleServiceError | null]> => {
  try {
    const config = {
      url,
      ...options,
    };
    const response = await axios.request(config);

    const data = response.data.data;

    if (get(response, "status") !== 200) {
      return [
        null,
        {
          code: get(response, "status", 500),
          message: get(data, "message", null),
          ...getErrorReportFields(response.data),
        },
      ];
    }

    return [data, null]; // Success response with no error
  } catch (error) {
    // If the error is a network error, prompt the user with a retry
    if (axios.isAxiosError(error) && get(error, "code") === "ERR_NETWORK") {
      console.error("Connection interrupted");
      return [null, { code: null, message: "Connection interrupted" }];
    }

    //If axios has API `response` error message, (i.e. `message` such as "Oops..." coming from Axle API), we should use that
    //If error occurred to due implementation error (e.g. axios config was malformed), we will not have response message
    const errorMessage = get(
      error,
      "response.data.message",
      get(error, "message", null)
    );
    const errorCode = get(error, "response.status", get(error, "status", null));

    return [
      null,
      {
        code: errorCode,
        message: errorMessage,
        ...getErrorReportFields(get(error, "response.data")),
      },
    ];
  }
};

export const callAxleService = async (
  url: string,
  options: Record<string, any> = {},
  httpClient: "fetch" | "axios" = "fetch",
  timeoutMs?: number
): Promise<[any, AxleServiceError | null]> => {
  switch (httpClient) {
    case "axios":
      return await axiosClient(url, options);
    case "fetch":
      return await fetchClient(url, options, timeoutMs);
  }
};

/**
 * Retrieve the session data for a given ignition token
 * @param ignitionToken - The unique token for the Ignition session.
 * @returns an Ignition Session object.
 */
export const getSession = async (ignitionToken: string) => {
  return await callAxleService(`${ibBaseUrl}/session/${ignitionToken}`, {
    method: "GET",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers": "*",
    },
  });
};

/**
 * `getAccount` is a function that takes in a carrier, username, and password and returns a JSON object
 * with the account information
 * @param ignitionToken - The unique token for the Ignition session.
 * @param carrier - The name of the carrier you want to get the account for.
 * @param username - The username of the account you want to retrieve.
 * @param password - The password for the account.
 * @param zipcode - The zipcode of the account.
 * @param account - The account ID. (Used for re-login)
 * @param token - The browser cookie JWT for the account. (Used for re-login)
 */
export const login = async (
  ignitionToken: string,
  carrier: string,
  username?: string,
  password?: string,
  zipcode?: string,
  account?: string,
  token?: string,
  integration?: string
) => {
  return await callAxleService(`${ibBaseUrl}/login`, {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-ignition-token": ignitionToken,
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers": "*",
    },
    body: JSON.stringify({
      carrier,
      username,
      password,
      zipcode,
      account,
      token,
      integration,
    }),
  });
};

/**
 * It takes an ignition token and returns an account object
 * @param ignitionToken - The token you received from the user when they logged in.
 * @returns the data object from the body of the response.
 */
export const linkAccount = async (ignitionToken: string) => {
  return await callAxleService(`${ibBaseUrl}/accounts`, {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-ignition-token": ignitionToken,
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers": "*",
    },
  });
};

/**
 * It returns a list of available MFA options for the user.
 * @param ignitionToken - The unique token for the Ignition session.
 * @param carrier - The name of the carrier.
 */
export const getMfaOptions = async (
  ignitionToken: string,
  carrier: string,
  integration: string
) => {
  return await callAxleService(`${ibBaseUrl}/mfa/options`, {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-ignition-token": ignitionToken,
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers": "*",
    },
    body: JSON.stringify({
      carrier,
      integration,
    }),
  });
};

/**
 * Send an MFA code to the user
 * @param ignitionToken - The unique token for the Ignition session.
 * @param carrier - The carrier that the user is trying to sign up with.
 * @param type - The type of MFA that you want to send.
 * @param id - The id of the MFA device
 */
export const sendMfa = async (
  ignitionToken: string,
  carrier: string,
  integration: string,
  type: string,
  id: string
) => {
  return await callAxleService(`${ibBaseUrl}/mfa/send`, {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-ignition-token": ignitionToken,
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers": "*",
    },
    body: JSON.stringify({
      carrier,
      type,
      id,
      integration,
    }),
  });
};

/**
 * It takes in a carrier, username, type, and code and sends it to the API
 * @param ignitionToken - The unique token for the Ignition session.
 * @param carrier - The carrier that the user is trying to log in to.
 * @param type - The type of MFA code you are entering.
 * @param code - The code that you received from the MFA device.
 * @returns The data object is being returned.
 */
export const enterMfa = async (
  ignitionToken: string,
  carrier: string,
  integration: string,
  type: string,
  code: string
) => {
  return await callAxleService(`${ibBaseUrl}/mfa/enter`, {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-ignition-token": ignitionToken,
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers": "*",
    },
    body: JSON.stringify({
      carrier,
      type,
      code,
      integration,
    }),
  });
};

/**
 * It links a policy to an account.
 * @param ignitionToken - The unique token for the Ignition session.
 * @param policyType - The type of the policy you want to link.
 * @param policyNumber - The policy number of the policy you want to link to the account.
 */
export const linkPolicy = async (
  ignitionToken: string,
  policyType: string,
  policyNumber: string
) => {
  return await callAxleService(`${ibBaseUrl}/policies`, {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-ignition-token": ignitionToken,
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers": "*",
    },
    body: JSON.stringify({
      policyType,
      policyNumber,
    }),
  });
};

export const linkDocuments = async (
  ignitionToken: string,
  policyType: string,
  policyNumber: string,
  policy: string
) => {
  return await callAxleService(`${ibBaseUrl}/documents`, {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-ignition-token": ignitionToken,
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers": "*",
    },
    body: JSON.stringify({
      policyType,
      policyNumber,
      policy,
    }),
  });
};

/**
 * This function stubs an account for a carrier.
 * @param ignitionToken - The token you received from the login call.
 * @param account - The account object.
 * @param processDetail - The process detail string.
 * @returns The stubbed account
 */
export const stubAccount = async (
  ignitionToken: string,
  account: Record<string, any>,
  processDetail: string
) => {
  return await callAxleService(`${ibBaseUrl}/stub/accounts`, {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-ignition-token": ignitionToken,
      "x-process-detail": processDetail,
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers": "*",
    },
    // Need to standardize these for all carriers...
    body: JSON.stringify(account),
  });
};

export const updateAccount = async (
  ignitionToken: string,
  accountId: string,
  account: Record<string, any>
) => {
  return await callAxleService(`${ibBaseUrl}/accounts/${accountId}`, {
    method: "post",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-ignition-token": ignitionToken,
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers": "*",
    },
    body: JSON.stringify(account),
  });
};

/**
 * It takes a policy object and returns a stubbed policy object
 * @param ignitionToken - The token you received from the login call.
 * @param policy - The policy object.
 * @param processDetail - The process detail string.
 * @returns The stubbed policy
 */
export const stubPolicy = async (
  ignitionToken: string,
  policy: Record<string, any>,
  processDetail: string
) => {
  return await callAxleService(`${ibBaseUrl}/stub/policies`, {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-ignition-token": ignitionToken,
      "x-process-detail": processDetail,
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers": "*",
    },
    // Need to standardize these for all carriers...
    body: JSON.stringify(policy),
  });
};

/**
 * It takes an ignition token, policy number, filename, and filetype, and returns a document object
 * @param ignitionToken - The token you received from the login function
 * @param policyId - The policy ID.
 * @param account - The account ID.
 * @param policy - The policy object.
 * @returns The document ID
 */
export const updatePolicy = async (
  ignitionToken: string,
  policyId: string,
  account: string,
  policy: Record<string, any>
) => {
  return await callAxleService(`${ibBaseUrl}/policies/${policyId}`, {
    method: "post",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-ignition-token": ignitionToken,
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers": "*",
    },
    body: JSON.stringify({
      account,
      ...policy,
    }),
  });
};

/**
 * It takes an ignition token, a policy ID, and a policy object, and then it updates the policy with
 * the new policy object
 * @param ignitionToken - The token you received from the login function
 * @param policyId - The ID of the policy you want to update.
 * @param policy - This is the policy object that you want to update.
 * @returns The policy elements are being returned.
 */
export const upsertPolicyElements = async (
  ignitionToken: string,
  policyId: string,
  policy: Record<string, any>
) => {
  return await callAxleService(`${ibBaseUrl}/policies/${policyId}`, {
    method: "put",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-ignition-token": ignitionToken,
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers": "*",
    },
    body: JSON.stringify(policy),
  });
};

/**
 * Validate if a policy meets certain requirements.
 * @param ignitionToken - token for the ignition session
 * @param policyId - policy ID for the policy you'd like to validate
 * @param validationType - validation type - check backend documentation for supported validation types
 * @returns validation results based on type
 */
export const validatePolicy = async (
  ignitionToken: string,
  policyId: string,
  validationType: string
) => {
  return await callAxleService(`${ibBaseUrl}/policies/${policyId}/validate`, {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-ignition-token": ignitionToken,
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers": "*",
    },
    body: JSON.stringify({ type: validationType }),
  });
};

/**
 * It takes an ignition token and a params object, and returns the updated ignition
 * @param ignitionToken - The token that was returned from the createIgnition call.
 * @param params - The params object.
 * @returns the data from the body of the response.
 */
export const updateIgnition = async (
  ignitionToken: string,
  params: Record<string, any>
) => {
  return await callAxleService(
    `${ibBaseUrl}/ignition/${ignitionToken}`,
    {
      method: "post",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Headers": "*",
      },
      body: JSON.stringify(params),
    },
    "fetch",
    UPDATE_IGNITION_TIMEOUT_MS
  );
};

/**
 * It takes in a params object, makes a POST request to the Ignition API, and returns the data from the
 * response
 * @param params - The params object.
 * @returns A promise that resolves to the data object from the response body.
 */
export const createIgnition = async (params: Record<string, any>) => {
  return await callAxleService(`${ibBaseUrl}/ignition`, {
    method: "post",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers": "*",
    },
    body: JSON.stringify(params),
  });
};

export const deleteIgnition = async (
  ignitionToken: string,
  clientId: string
) => {
  return await callAxleService(`${ibBaseUrl}/ignitions/${ignitionToken}`, {
    method: "delete",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers": "*",
      "x-client-id": clientId,
    },
  });
};

/**
 * It takes an account and client, and returns an authCode
 * @param account - The account ID of the user you want to get an auth code for.
 * @param policies - The policies object.
 * @param client - The client ID of the application you're using.
 * @param ignitionToken - The ignition token.
 * @returns The authCode is being returned.
 */
export const getAccessToken = async (
  account: string,
  policies: Record<string, any>,
  client: string,
  ignitionToken: string
) => {
  // We need to show some error to the consumer if this fails
  const [data, error] = (await callAxleService(`${ibBaseUrl}/accessTokens`, {
    method: "post",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-ignition-token": ignitionToken,
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers": "*",
    },
    body: JSON.stringify({
      client,
      policies,
      account,
    }),
  })) as any;

  // Return authCode
  const authCode = get(data, "authCode", null);
  const ruxTokenData = get(data, "tokenData", null);
  return { authCode, ruxTokenData, error };
};

/**
 * It takes in an Ignition token, a policy, a filename, and a filetype, and returns a signed URL
 * @param ignitionToken - The token you get from the `/auth` endpoint
 * @param policy - The policy number you want to upload the document to.
 * @param filepath - The file path of the file you're uploading.
 * @param useTemporaryLocation - Whether to upload to temp directory
 * @returns The signed URL for the file to be uploaded.
 */
export const generateSignedUrl = async (
  ignitionToken: string,
  policy: string,
  filepath: string,
  useTemporaryLocation: boolean = false
) => {
  // We need to show some error to the consumer if this fails
  return await callAxleService(
    `${ibBaseUrl}/generateSignedUrl`,
    {
      method: "post",
      headers: {
        "Content-Type": "application/json",
        "x-ignition-token": ignitionToken,
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Headers": "*",
      },
      data: JSON.stringify({
        policy,
        filepath,
        useTemporaryLocation,
      }),
    },
    "axios"
  );
};

export const uploadDocument = async (signedUrl: string, fileData: any) => {
  const contentType = fileData?.type || "application/octet-stream";

  return await callAxleService(
    signedUrl,
    {
      method: "put",
      data: fileData,
      headers: { "Content-Type": contentType },
    },
    "axios"
  );
};

export const processDocuments = async (
  ignitionToken: string,
  documentKey: string,
  documentsObject: Record<string, any>[],
  policyTypes: PolicyType[]
) => {
  return await callAxleService(
    `${ibBaseUrl}/documents/process`,
    {
      method: "post",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        "x-ignition-token": ignitionToken,
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Headers": "*",
      },
      data: JSON.stringify({
        documentKey,
        documentsObject,
        policyTypes,
      }),
    },
    "axios"
  );
};

export const processAlternativeLink = async (
  ignitionToken: string,
  carrier: string,
  integration: string,
  alternativePortalInput: Record<string, any>
) => {
  return await callAxleService(
    `${ibBaseUrl}/link`,
    {
      method: "post",
      mode: "cors",
      headers: {
        "Content-Type": "application/json",
        "x-ignition-token": ignitionToken,
        "Access-Control-Allow-Origin": "*",
        "Access-Control-Allow-Headers": "*",
      },
      data: JSON.stringify({
        carrier,
        alternativePortalInput,
        integration,
      }),
    },
    "axios"
  );
};

export const processDirectLink = async (
  ignitionToken: string,
  integration: string,
  operation: Operation,
  data:
    | LoginOperation
    | GetMfaOptionsOperation
    | EnterMfaOperation
    | SendMfaOperation
    | GetPolicyOperation
    | SubmitCredentialsOperation
    | ResendMfaOperation
) => {
  return await callAxleService(`${ibBaseUrl}/direct-link`, {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-ignition-token": ignitionToken,
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers": "*",
    },
    body: JSON.stringify({
      integration,
      data,
      operation
    }),
  });
};

/**
 * Decodes the JWT token and returns the payload necessary for WelcomeBack page
 * @param token - The Cognito JWT ID Token that contains the user attributes
 * @returns An object containing the verification result and account information if valid
 */
export const verifyDeviceToken = async (
  token: string,
  ignitionToken: string
) => {
  return await callAxleService(`${ibBaseUrl}/verify-device-token`, {
    method: "POST",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers": "*",
      "x-ignition-token": ignitionToken,
    },
    body: JSON.stringify({ token }),
  });
};

export const combineImagesToPDF = async (
  token: string,
  documentKeys: { key: string }[]
) => {
  return await callAxleService(`${ibBaseUrl}/documents/combineImagesIntoPdf`, {
    method: "post",
    mode: "cors",
    headers: {
      "Content-Type": "application/json",
      "x-ignition-token": token,
      "Access-Control-Allow-Origin": "*",
      "Access-Control-Allow-Headers": "*",
    },
    body: JSON.stringify({
      documentPages: documentKeys,
    }),
  });
};

